import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Role {
  roleId: string;
  roleName: string;
}

interface Location {
  locationId: string;
  locationName: string;
}

const EditUser: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    roleId: '',
    locationId: '',
    isActive: true,
    dvlaAccessEnabled: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        // Fetch user data
        const userResponse = await axios.get(`${API_BASE_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Fetch roles and locations
        const [rolesResponse, locationsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/roles`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/locations`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        const filteredRoles = rolesResponse.data.filter((role: Role) => role.roleName !== 'Super Admin');
        
        // Deduplicate roles by roleName - keep only the first occurrence of each role name
        const uniqueRolesMap = new Map<string, Role>();
        filteredRoles.forEach((role: Role) => {
          if (!uniqueRolesMap.has(role.roleName)) {
            uniqueRolesMap.set(role.roleName, role);
          }
        });
        const uniqueRoles = Array.from(uniqueRolesMap.values());
        
        // Match role by name instead of ID to handle duplicate roles in database
        // If user has role "Admin", find the first "Admin" role in the available roles list
        let matchedRoleId = String(userResponse.data.roleId || '');
        if (userResponse.data.role) {
          const matchingRole = uniqueRoles.find((r: Role) => r.roleName === userResponse.data.role);
          if (matchingRole) {
            matchedRoleId = String(matchingRole.roleId);
          }
        }
        
        // Do the same for location
        let matchedLocationId = String(userResponse.data.locationId || '');
        if (userResponse.data.locationName) {
          const matchingLocation = locationsResponse.data.find((l: Location) => l.locationName === userResponse.data.locationName);
          if (matchingLocation) {
            matchedLocationId = String(matchingLocation.locationId);
          }
        }
        
        const userData = {
          username: userResponse.data.username || '',
          email: userResponse.data.email || '',
          firstName: userResponse.data.firstName || '',
          lastName: userResponse.data.lastName || '',
          roleId: matchedRoleId,
          locationId: matchedLocationId,
          isActive: Boolean(userResponse.data.isActive),
          dvlaAccessEnabled: Boolean(userResponse.data.dvlaAccessEnabled),
        };
        
        setFormData(userData);
        setRoles(uniqueRoles);
        setLocations(locationsResponse.data);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          setError('Session expired. Please log in again.');
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/';
          }, 2000);
        } else if (error.response?.status === 403) {
          setError('You do not have permission to edit users.');
        } else if (error.response?.status === 404) {
          setError('User not found.');
        } else {
          setError('Failed to load user data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      await axios.put(`${API_BASE_URL}/users/${userId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      navigate('/admin/users');
    } catch (error: any) {
      console.error('Error updating user:', error);
      setError(error.response?.data?.message || 'Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/admin/users')}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Edit User
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleSelectChange}
                  label="Role"
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  name="locationId"
                  value={formData.locationId}
                  onChange={handleSelectChange}
                  label="Location"
                >
                  {locations.map((location) => (
                    <MenuItem key={location.locationId} value={location.locationId}>
                      {location.locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.isActive)}
                    onChange={handleChange}
                    name="isActive"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.dvlaAccessEnabled)}
                    onChange={handleChange}
                    name="dvlaAccessEnabled"
                  />
                }
                label="DVLA Access"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                When enabled, this user can view DVLA information, management screens, and receive DVLA notifications.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/users')}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default EditUser; 